import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data && (data as { codErro?: number }).codErro !== undefined && data.codErro! > 0) {
        return { retornoErro: data.codErro!, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

// ─── Modelos ─────────────────────────────────────────────────────────────────

/** Um usuário do painel (entidade Fornecedor no backend). */
export interface UsuarioPainel {
    id: number
    nome: string
    email: string
    telefone: string | null
    perfil: string
    ativo: boolean
    receberEmailPedidoExpirado: boolean
    receberEmailPedidoRealizado: boolean
    receberEmailPedidoCancelado: boolean
    reenvioImediatoCodigoAutenticacao: boolean
}

export interface NovoUsuarioPainel {
    id?: number
    nome: string
    email: string
    telefone?: string
    perfil: string
    /** Vazio em edição mantém a senha atual; obrigatório na criação. */
    senha?: string
    receberEmailPedidoExpirado: boolean
    receberEmailPedidoRealizado: boolean
    receberEmailPedidoCancelado: boolean
    reenvioImediatoCodigoAutenticacao: boolean
}

export interface CredenciaisUsuario {
    email: string
    senha: string
    tokenIntegracao: string
}

/** Perfis oferecidos na tela — os mesmos ativos no getPerfilModel do Tapestry. */
export const PERFIS_USUARIO: { valor: string; rotulo: string }[] = [
    { valor: "CLIENTEFIEL", rotulo: "Cliente Fiel" },
    { valor: "ADMINISTRADOR", rotulo: "Administrador" },
    { valor: "DELIVERY_BASICO", rotulo: "Delivery Básico" },
    { valor: "DELIVERY_CARDAPIO", rotulo: "Delivery Cardápio" },
    { valor: "WHATSAPP", rotulo: "WhatsApp" },
]

// ─── Chamadas ────────────────────────────────────────────────────────────────

export const listarUsuarios = async function (
    authorization: string
): Promise<UsuarioPainel[] | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<UsuarioPainel[]>>("usuariospainel/listar", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<UsuarioPainel[]>(response, "listarUsuarios")
    } catch (error) {
        RegistrarLogErro(error, "listarUsuarios", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarUsuario = async function (
    authorization: string,
    usuario: NovoUsuarioPainel
): Promise<UsuarioPainel | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<UsuarioPainel>>("usuariospainel/salvar", usuario, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<UsuarioPainel>(response, "salvarUsuario")
    } catch (error) {
        RegistrarLogErro(error, "salvarUsuario", JSON.stringify({ ...usuario, senha: undefined }))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const desativarUsuario = async function (
    authorization: string,
    id: number
): Promise<{ id: number; ativo: boolean } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ id: number; ativo: boolean }>>(
                `usuariospainel/desativar/${id}`,
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<{ id: number; ativo: boolean }>(response, "desativarUsuario")
    } catch (error) {
        RegistrarLogErro(error, "desativarUsuario", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const enviarEmailBoasVindas = async function (
    authorization: string,
    id: number
): Promise<{ enviado: boolean } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ enviado: boolean }>>(
                `usuariospainel/email-boas-vindas/${id}`,
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<{ enviado: boolean }>(response, "enviarEmailBoasVindas")
    } catch (error) {
        RegistrarLogErro(error, "enviarEmailBoasVindas", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterCredenciais = async function (
    authorization: string,
    id: number
): Promise<CredenciaisUsuario | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<CredenciaisUsuario>>(`usuariospainel/credenciais/${id}`, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<CredenciaisUsuario>(response, "obterCredenciais")
    } catch (error) {
        RegistrarLogErro(error, "obterCredenciais", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
